import React from 'react';
import { Mail, Phone, MapPin, Laptop, ShieldCheck } from 'lucide-react';
import { AppView } from '../App';
import Logo from './Logo';

interface FooterProps {
  activeView?: AppView;
  onViewChange?: (view: AppView) => void;
  onOpenLegal?: (type: 'privacy' | 'kvkk' | 'terms') => void;
}

export default function Footer({ activeView, onViewChange, onOpenLegal }: FooterProps) {
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    if (activeView && activeView !== 'landing' && onViewChange) {
      onViewChange('landing');
      setTimeout(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLegalClick = (e: React.MouseEvent<HTMLAnchorElement>, type: 'privacy' | 'kvkk' | 'terms') => {
    e.preventDefault();
    if (onOpenLegal) {
      onOpenLegal(type);
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800">
      
      {/* Top Section - CTA */}
      <div className="border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">Saha Operasyonlarınızı Dijitalleştirin</h3>
              <p className="text-slate-400 text-sm md:text-base max-w-xl">
                Sınırsız kullanıcı, tam veri egemenliği ve taşeron gizliliği ile OmniOps® işletmenizin yeni nesil işletim sistemidir.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <button 
                onClick={() => onViewChange && onViewChange('sandbox')}
                className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-4 md:py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 min-h-[44px]"
              >
                Etkileşimli Turu Başlat
              </button>
              <a 
                href="#teklif"
                onClick={(e) => handleNavClick(e, 'teklif')}
                className="bg-[#E30613] hover:bg-red-700 text-white px-6 py-4 md:py-3 rounded-xl font-semibold transition-colors shadow-lg shadow-red-500/20 flex items-center justify-center gap-2 min-h-[44px]"
              >
                Altyapı Teklifi Alın
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          
          {/* Company Bio */}
          <div className="md:col-span-4 space-y-4">
            <div className="flex items-center gap-3">
              <Logo className="h-8 md:h-10 w-auto filter brightness-0 invert opacity-90" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              1995 yılından beri Türkiye bilişim sektöründe güvenilir çözüm ortağınız. 
              Kurumsal IT destek, ağ güvenliği, sistem ve donanım desteği hizmetleri sunuyoruz.
            </p>
            <div className="flex items-center gap-2 pt-2 text-xs text-slate-400 font-mono">
              <ShieldCheck className="h-4 w-4 text-sky-500" />
              <span>%100 Yerli ve Güvenli Entegrasyon</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2 space-y-4">
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase">Kurumsal</h4>
            <ul className="space-y-1 text-sm">
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Hizmetlerimiz</a></li>
              <li><a href="#kurumsal" onClick={(e) => handleNavClick(e, 'kurumsal')} className="hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Hakkımızda</a></li>
              <li><a href="#omniops" onClick={(e) => handleNavClick(e, 'omniops')} className="hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">OmniOps® OS</a></li>
              <li><a href="#usps" onClick={(e) => handleNavClick(e, 'usps')} className="hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Teknolojimiz</a></li>
            </ul>
          </div>

          {/* Service Links */}
          <div className="md:col-span-2 space-y-4">
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase">Hizmetlerimiz</h4>
            <ul className="space-y-1 text-sm">
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-slate-400 hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Ağ Güvenliği</a></li>
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-slate-400 hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Donanım Desteği</a></li>
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-slate-400 hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Sistem Desteği</a></li>
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-slate-400 hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Network Sistemleri</a></li>
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-slate-400 hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Yazılım Desteği</a></li>
              <li><a href="#servisler" onClick={(e) => handleNavClick(e, 'servisler')} className="text-slate-400 hover:text-white transition-colors block py-2 md:py-1 min-h-[44px] flex items-center md:min-h-0 md:inline-flex">Sarf Malzemesi Temini</a></li>
            </ul>
          </div>

          {/* Direct Contacts */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase">İletişim Bilgileri</h4>
            <ul className="space-y-3.5 text-sm font-mono">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
                <span className="text-slate-400 leading-relaxed font-sans">
                  Dere Mahallesi Selanik Sokak Kardelen Apt.<br/> No:5/5 Merkez/YALOVA
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />
                <span className="text-slate-400 leading-relaxed font-sans">
                  Bilgisayar Bölümü<br/>+90 549 359 85 96<br/>+90 549 385 84 87
                </span>
              </li>
              <li className="flex flex-col gap-1.5 mt-2">
                <div className="flex items-center gap-2.5">
                  <Mail className="h-4 w-4 text-sky-400" />
                  <span className="text-slate-400">Genel İletişim:</span>
                </div>
                <a href="mailto:alternatif.omniops@gmail.com" className="text-slate-400 pl-6.5 hover:text-white transition-colors">alternatif.omniops@gmail.com</a>
                
                <div className="flex items-center gap-2.5 mt-2">
                  <Mail className="h-4 w-4 text-sky-400" />
                  <span className="text-slate-400">OmniOps® Bilgi:</span>
                </div>
                <a href="mailto:omniops.fs.os@gmail.com" className="text-slate-400 pl-6.5 hover:text-white transition-colors">omniops.fs.os@gmail.com</a>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom Legal Band */}
      <div className="bg-slate-950 py-6 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-400 font-mono">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <span>&copy; {new Date().getFullYear()} Alternatif Bilgisayar Elk. Elektrotek. İnş. San. Tic. Ltd. Şti. Tüm hakları saklıdır.</span>
            <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2.5 text-[11px] text-slate-400 font-sans">
              <a href="#privacy" onClick={(e) => handleLegalClick(e, 'privacy')} className="hover:text-white transition-colors">Gizlilik Politikası</a>
              <span className="text-slate-700">|</span>
              <a href="#kvkk" onClick={(e) => handleLegalClick(e, 'kvkk')} className="hover:text-white transition-colors">KVKK Aydınlatma Metni</a>
              <span className="text-slate-700">|</span>
              <a href="#terms" onClick={(e) => handleLegalClick(e, 'terms')} className="hover:text-white transition-colors">Kullanım Koşulları</a>
            </div>
          </div>
          <div className="text-slate-400 font-sans shrink-0">
            Dizayn & Mühendislik: Alternatif
          </div>
        </div>
      </div>

    </footer>
  );
}
